import { z } from "zod";
import { Id, IsoDateTime } from "./common";
import { MaintenanceState } from "./asset";

/**
 * Device = the *physical telemetry source* (camera, sensor, gateway). Devices
 * are tenant-owned and may be assigned to one Asset at a time.
 *
 * The active assignment lives in {@link DeviceAssignment} on the asset PK so
 * `Query(PK = ASSET#..., SK begins_with DEVICE_ASSIGNMENT#ACTIVE)` returns
 * the live set without scanning. Historical assignments will be added in a
 * later slice.
 */
export const DeviceStatus = z.enum([
  "Active",
  "Disabled",
  "Archived",
  "Retired",
]);
export type DeviceStatus = z.infer<typeof DeviceStatus>;

/**
 * DeviceLifecycleState tracks the operational lifecycle of a device record,
 * distinct from DeviceStatus (which is the current operational health).
 *
 * Discovered:     Known to an integration but not yet mapped or registered.
 * PendingMapping: Unmapped entity approved for registration; awaiting operator
 *                 link/create action to assign an internal deviceId.
 * Active:         Fully registered and mapped; telemetry expected.
 * Disabled:       Temporarily suspended; no telemetry expected but record kept.
 * Retired:        End-of-life; replaced by a newer device or removed from service.
 * Replaced:       Superseded by another deviceId (e.g. hardware swap); historical.
 * Archived:       Soft-deleted; removed from operational views but auditable.
 *
 * State transitions:
 *   Discovered → PendingMapping → Active
 *   Active → Disabled → Active (operator-controlled)
 *   Active → Retired | Replaced | Archived
 *   Integration delete → Disabled (not hard-delete) unless platform policy allows
 */
export const DeviceLifecycleState = z.enum([
  "Discovered",
  "PendingMapping",
  "Active",
  "Disabled",
  "Retired",
  "Replaced",
  "Archived",
]);
export type DeviceLifecycleState = z.infer<typeof DeviceLifecycleState>;

export const DeviceType = z.enum([
  "Camera",
  "Sensor",
  "Gateway",
  "Telematics",
  "EnergyMeter",
  "DoorSensor",
  "Other",
]);
export type DeviceType = z.infer<typeof DeviceType>;

export const CommunicationType = z.enum([
  "Cellular",
  "Wifi",
  "Ethernet",
  "Lora",
  "Satellite",
  "Bluetooth",
  "Other",
]);
export type CommunicationType = z.infer<typeof CommunicationType>;

export const Device = z.object({
  deviceId: Id,
  tenantId: Id,
  deviceType: DeviceType,
  name: z.string().min(1).max(120),
  normalizedName: z.string().min(1).max(120),
  status: DeviceStatus,
  manufacturer: z.string().max(120).nullable(),
  model: z.string().max(120).nullable(),
  serialNumber: z.string().max(120).nullable(),
  firmwareVersion: z.string().max(120).nullable(),
  hardwareVersion: z.string().max(120).nullable(),
  communicationType: CommunicationType,
  maintenanceState: MaintenanceState,
  /** Optional reference to a DeviceTypeProfile in the catalog. Null falls back to generic.v1. */
  profileId: z.string().min(1).nullable().optional(),
  /**
   * Lifecycle state distinct from operational status. Active devices have
   * lifecycleState = Active. Null is treated as Active for backward compat.
   */
  lifecycleState: DeviceLifecycleState.nullable().optional(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  createdBy: Id.nullable(),
  updatedBy: Id.nullable(),
});
export type Device = z.infer<typeof Device>;

export const DeviceCreateInput = z.object({
  deviceType: DeviceType,
  name: z.string().min(1).max(120),
  manufacturer: z.string().max(120).nullable().optional(),
  model: z.string().max(120).nullable().optional(),
  serialNumber: z.string().max(120).nullable().optional(),
  firmwareVersion: z.string().max(120).nullable().optional(),
  hardwareVersion: z.string().max(120).nullable().optional(),
  communicationType: CommunicationType.optional(),
  maintenanceState: MaintenanceState.optional(),
});
export type DeviceCreateInput = z.infer<typeof DeviceCreateInput>;

export const DeviceUpdateInput = z
  .object({
    deviceType: DeviceType,
    name: z.string().min(1).max(120),
    status: DeviceStatus,
    manufacturer: z.string().max(120).nullable(),
    model: z.string().max(120).nullable(),
    serialNumber: z.string().max(120).nullable(),
    firmwareVersion: z.string().max(120).nullable(),
    hardwareVersion: z.string().max(120).nullable(),
    communicationType: CommunicationType,
    maintenanceState: MaintenanceState,
  })
  .partial();
export type DeviceUpdateInput = z.infer<typeof DeviceUpdateInput>;

export const DeviceAssignmentStatus = z.enum(["Active", "Ended"]);
export type DeviceAssignmentStatus = z.infer<typeof DeviceAssignmentStatus>;

export const DeviceAssignment = z.object({
  deviceAssignmentId: Id,
  tenantId: Id,
  deviceId: Id,
  assetId: Id,
  siteId: Id,
  assignedAt: IsoDateTime,
  assignedBy: Id.nullable(),
  unassignedAt: IsoDateTime.nullable(),
  unassignedBy: Id.nullable(),
  assignmentStatus: DeviceAssignmentStatus,
  assignmentReason: z.string().max(500).nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type DeviceAssignment = z.infer<typeof DeviceAssignment>;

export const DeviceAssignInput = z.object({
  deviceId: Id,
  assetId: Id,
  reason: z.string().min(1).max(500).optional(),
});
export type DeviceAssignInput = z.infer<typeof DeviceAssignInput>;

export const DeviceUnassignInput = z.object({
  deviceAssignmentId: Id,
  reason: z.string().min(1).max(500).optional(),
});
export type DeviceUnassignInput = z.infer<typeof DeviceUnassignInput>;
